/**
 * Pure geometry for the field plot: the grid-space ↔ screen transform, and a uniform-grid
 * spatial index over the nodes.
 *
 * Split out of `plot.ts` because this is the part worth checking — no DOM, no app imports, so
 * `frontend/scripts/plot-math.check.mts` runs it under plain `node`.
 *
 * "Grid space" is the space the plot is linear in: `rx` = log10(1 + velocity) (the x axis is
 * log-scaled) and `frac` = 0…1 of the sqrt value/inventory domain. Both are precomputed once
 * per SKU, so a pan/zoom frame is one multiply-add per node and the index stays valid at every
 * zoom level (the view only ever scales grid space affinely).
 */

/** `px = rx * kx + bx` · `py = frac * ky + by` (ky is negative — screen y grows downward). */
export interface Transform {
  kx: number;
  bx: number;
  ky: number;
  by: number;
}

export interface Viewport {
  /** Grid-space window. */
  rx0: number;
  rx1: number;
  f0: number;
  f1: number;
  /** Pixel box it maps onto. */
  left: number;
  right: number;
  top: number;
  bottom: number;
}

export function makeTransform(v: Viewport): Transform {
  const kx = (v.right - v.left) / (v.rx1 - v.rx0 || 1);
  const sy = (v.bottom - v.top) / (v.f1 - v.f0 || 1);
  return { kx, bx: v.left - v.rx0 * kx, ky: -sy, by: v.bottom + v.f0 * sy };
}

/**
 * Velocity → grid-space x, and back.
 *
 * `log10(1 + v)` rather than `log10(v)`: velocity is a rate that legitimately reaches 0 and dips
 * below 1 at the tail, both of which plain log has no answer for. The +1 keeps the transform
 * defined and monotone over the whole domain, and pins the axis origin at velocity 0 — while the
 * SKUs that are *exactly* 0 are lifted out into the gutter, where they read as a cohort instead of
 * a smear on the axis line.
 */
export const toLx = (v: number): number => Math.log10(1 + (v > 0 ? v : 0));
export const fromLx = (l: number): number => Math.pow(10, l) - 1;

/**
 * Does this SKU belong in the non-mover gutter rather than on the axis?
 *
 * The gutter means "did not ship in the window", which is what `lastMovedQ` states outright —
 * an exact-zero velocity is only one way a provider can say it. A derived rate is a float, and a
 * provider that models a dormant SKU as 0.4 boxes/month would otherwise leave the gutter empty
 * under a label counting non-movers.
 *
 * The velocity half is written as `!(v > 0)` so a NaN or missing velocity lands in the gutter too
 * — an unplottable value must be somewhere the user can see and select it, never quietly at x = 0.
 */
export const inGutter = (vel: number, lastMovedQ = 0): boolean => !(vel > 0) || lastMovedQ >= 1;

/**
 * Fit `[a, b]` — in either order — into a window at least `min` wide, kept inside `[lo, hi]`.
 *
 * The one place both zoom gestures agree on their floor: a wheel notch and a drawn box both hand
 * over a raw span and get back one that is non-degenerate, non-inverted, no wider than the domain
 * and still inside it. A box narrower than `min` (down to a single pixel) clamps to `min` around
 * its own centre instead of collapsing.
 */
export function fitWindow(
  a: number,
  b: number,
  min: number,
  lo: number,
  hi: number,
): [number, number] {
  if (!Number.isFinite(a) || !Number.isFinite(b)) {
    return [lo, hi];
  }
  const clamp = (v: number) => (v < lo ? lo : v > hi ? hi : v);
  const x0 = clamp(Math.min(a, b));
  const x1 = clamp(Math.max(a, b));
  const span = Math.min(hi - lo, Math.max(min, x1 - x0));
  const c = (x0 + x1) / 2;
  let n0 = c - span / 2;
  let n1 = c + span / 2;
  // Slid back inside the domain rather than truncated, so the span survives the clamp.
  if (n0 < lo) {
    n1 += lo - n0;
    n0 = lo;
  }
  if (n1 > hi) {
    n0 -= n1 - hi;
    n1 = hi;
  }
  return [clamp(n0), clamp(n1)];
}

/**
 * Deterministic 0…1 position across the gutter column for node `i` — the golden-ratio sequence,
 * which spreads consecutive indices apart without a random source, so the same input always
 * paints the same picture.
 */
export const gutterFrac = (i: number): number => (i * 0.6180339887498949) % 1;

export const toPx = (t: Transform, rx: number): number => rx * t.kx + t.bx;
export const toPy = (t: Transform, f: number): number => f * t.ky + t.by;
export const toRx = (t: Transform, px: number): number => (px - t.bx) / t.kx;
export const toFrac = (t: Transform, py: number): number => (py - t.by) / t.ky;

/**
 * Bucketed node index in CSR layout — one flat `items` array plus row starts, so there are no
 * nested arrays to allocate and nothing for the GC to chase on rebuild.
 */
export interface Grid {
  cols: number;
  rows: number;
  /** `rx` span the columns cover; `frac` always spans 0…1. */
  rxMax: number;
  /** Bucket starts, length `cols * rows + 1`. */
  start: Int32Array;
  /** Node indices, bucket-major, ascending within a bucket. */
  items: Int32Array;
}

const clampCell = (v: number, count: number) => (v < 0 ? 0 : v >= count ? count - 1 : v);

export function buildGrid(rx: Float32Array, frac: Float32Array, rxMax: number): Grid {
  const n = rx.length;
  // Square, sized off the node count so the grid scales from a few hundred SKUs to 100k without
  // tuning. It is NOT ~2 nodes per bucket in practice — a real portfolio is heavily clustered:
  // measured at ~30k nodes (123×123 buckets) only ~18% of buckets are occupied, ~11 nodes each,
  // and the corner cell holds a few thousand, because the never-shipped SKUs all share rx 0 and
  // the zero-value Rx all share frac 0. A hover in that corner therefore distance-tests a few
  // thousand nodes — measured under 0.01 ms, which is why this stays a uniform grid. Bucket on
  // rank/quantile boundaries if a future feed makes that corner hotter still.
  // The 256 ceiling keeps `start` under ~256 KB.
  const side = Math.max(1, Math.min(256, Math.ceil(Math.sqrt(Math.max(1, n) / 2))));
  const span = rxMax > 0 ? rxMax : 1;
  const cells = side * side;
  const start = new Int32Array(cells + 1);
  const cellOf = (i: number) =>
    clampCell(Math.floor((rx[i]! / span) * side), side) * side +
    clampCell(Math.floor(frac[i]! * side), side);
  for (let i = 0; i < n; i++) {
    start[cellOf(i) + 1]!++;
  }
  for (let c = 0; c < cells; c++) {
    start[c + 1]! += start[c]!;
  }
  const cursor = Int32Array.from(start.subarray(0, cells));
  const items = new Int32Array(n);
  for (let i = 0; i < n; i++) {
    items[cursor[cellOf(i)]!++] = i;
  }
  return { cols: side, rows: side, rxMax: span, start, items };
}

/**
 * The pixel box the painter actually paints into. Both queries below take it, because a node
 * outside it is drawn nowhere: without it the index answers in grid space, which extends past the
 * view, and a node off the edge shadows the visible dot beside it (hover) or joins a selection the
 * user cannot see (marquee).
 */
export interface Clip {
  l: number;
  r: number;
  t: number;
  b: number;
}

/**
 * Screen-cell aggregation over the painted nodes — the cluster glyphs.
 *
 * A real portfolio is lens variants (power / cylinder / axis) of a handful of brand-and-pack
 * families, so thousands of SKUs share one (velocity, value) position: measured on the real drop,
 * 96% of SKUs sit on a position held by at least one other, and the painter stamps ~94% of its ink
 * under a dot already there. One mark then stands for anywhere between 1 and thousands of SKUs
 * while the panel beside it names an exact held count, and a client counting dots finds numbers
 * that do not reconcile.
 *
 * So the field paints one stamp per occupied screen cell, carries the cell's member count in the
 * stamp's opacity, and hands the members to hover and to the selection. Aggregation is on the
 * SCREEN cell, not on the family: zoom in and the cells subdivide, so a cluster dissolves back into
 * its members and the scatter keeps its distribution story.
 *
 * CSR layout, exactly like `Grid` above — one flat `items` array plus row starts, no nested arrays
 * to allocate and nothing for the GC to chase.
 */
export interface Cells {
  /** Cell side in CSS px. */
  cell: number;
  /** Origin and extent of the binned pixel box. */
  l: number;
  t: number;
  cols: number;
  rows: number;
  /** Occupied cell ids (`col + cols * row`), ascending. One entry per glyph. */
  id: Int32Array;
  /** Bucket starts into `items`, length `id.length + 1`. */
  start: Int32Array;
  /** Member node indices, **most decision-relevant first** inside each cell (see `binCells`). */
  items: Int32Array;
  /** Node index → its slot in `id`, or -1 for a node the painter drew nowhere. */
  slot: Int32Array;
}

/**
 * Bin the painted nodes onto `cell`-sized screen cells.
 *
 * `px`/`py` are the pixel coordinates the painter itself computed — not recomputed here — so the
 * glyphs can never disagree with the dots: a node binned into a cell is a node stamped on screen,
 * and `NaN` (the painter's cull marker) fails every comparison below and lands in no cell at all.
 *
 * `order` is the painter's draw order, plain → NPI → premium → worth, i.e. least to most
 * decision-relevant. Members are placed by walking it **backwards**, so `items[start[s]]` is the
 * cell's most decision-relevant member: the one whose sprite the glyph wears, and the head of the
 * list the panel shows. That is the same priority the painter already resolves overlaps by.
 *
 * Identity the whole feature rests on: `items.length` is the number of nodes on screen, so the sum
 * of the glyph counts equals the visible node count under any transform, mask or zoom.
 */
export function binCells(
  px: Float32Array,
  py: Float32Array,
  order: Uint32Array,
  vis: Uint8Array,
  box: Clip,
  cell: number,
): Cells {
  const n = px.length;
  const side = cell > 0 ? cell : 1;
  const cols = Math.max(1, Math.ceil((box.r - box.l) / side));
  const rows = Math.max(1, Math.ceil((box.b - box.t) / side));
  const cellN = cols * rows;
  // Doubles as the node → cell map on the way in and the node → slot map on the way out, so a
  // rebin allocates one array per node rather than two.
  const slot = new Int32Array(n).fill(-1);
  const off = new Int32Array(cellN + 1);
  for (let i = 0; i < n; i++) {
    if (!vis[i]) {
      continue;
    }
    const x = px[i]!;
    const y = py[i]!;
    if (!(x >= box.l && x <= box.r && y >= box.t && y <= box.b)) {
      continue;
    }
    const c =
      clampCell(Math.floor((x - box.l) / side), cols) +
      cols * clampCell(Math.floor((y - box.t) / side), rows);
    slot[i] = c;
    off[c + 1]!++;
  }
  for (let c = 0; c < cellN; c++) {
    off[c + 1]! += off[c]!;
  }
  const painted = off[cellN]!;
  const items = new Int32Array(painted);
  const cursor = Int32Array.from(off.subarray(0, cellN));
  // Backwards through the draw order: the counting sort is stable, so each cell ends up holding its
  // members most-relevant-first.
  for (let k = n - 1; k >= 0; k--) {
    const i = order[k]!;
    const c = slot[i]!;
    if (c >= 0) {
      items[cursor[c]!++] = i;
    }
  }
  // Compact to the occupied cells. `items` is already laid out in cell-id order, so an occupied
  // cell's dense start IS its bucket start.
  let occ = 0;
  for (let c = 0; c < cellN; c++) {
    if (off[c + 1]! > off[c]!) {
      occ++;
    }
  }
  const id = new Int32Array(occ);
  const start = new Int32Array(occ + 1);
  start[occ] = painted;
  let s = 0;
  for (let c = 0; c < cellN; c++) {
    if (off[c + 1]! > off[c]!) {
      id[s] = c;
      start[s] = off[c]!;
      s++;
    }
  }
  for (let k = 0; k < occ; k++) {
    for (let j = start[k]!, end = start[k + 1]!; j < end; j++) {
      slot[items[j]!] = k;
    }
  }
  return { cell: side, l: box.l, t: box.t, cols, rows, id, start, items, slot };
}

/**
 * How far a glyph's opacity is pulled off full for the deepest possible stack. Held here so the
 * self-check and the painter cannot disagree about the ramp.
 */
export const GLYPH_SPAN = 0.45;

/**
 * Opacity for a glyph standing for `count` SKUs out of a portfolio of `total`.
 *
 * `count === 1` returns exactly 1 — a lone SKU paints at the alpha it painted at before clustering
 * existed, which is what keeps a sparse field (the demo drop, and every zoomed-in view of a real
 * one) pixel-for-pixel the prototype.
 *
 * Above one the stamp lightens with `log10(count)`. Direction is forced and it is the honest one: a
 * dot is already opaque, so there is no headroom to make a deep stack *darker* — today a stack of
 * 1,000 and a single SKU paint the identical solid dot, which is exactly why a count cannot be read
 * off the field at all. Density still reads as density, through area: a busy region occupies every
 * cell around it, so it stays a solid mass. What the transparency adds is the depth *within* one
 * position — and on a log ramp 100, 1,000 and 9,545 stay distinguishable instead of all flattening
 * to the same saturated mark.
 *
 * Normalised by `total` (the portfolio) rather than by the heaviest cell on screen, so the alpha of
 * a 20-SKU stack is a fact about that stack and does not shift as you pan, zoom or filter past a
 * heavier one.
 */
export function glyphAlpha(count: number, total: number): number {
  if (!(count > 1)) {
    return 1;
  }
  // A drop with fewer than 10 SKUs would otherwise divide by ~0 and slam every pair to the floor.
  const span = Math.log10(Math.max(10, total));
  return 1 - GLYPH_SPAN * Math.min(1, Math.log10(count) / span);
}

const outside = (c: Clip, px: number, py: number) => px < c.l || px > c.r || py < c.t || py > c.b;

/** Bucket range covering `[lo, hi]` of a 0…span axis, clamped to the grid. */
function range(lo: number, hi: number, span: number, count: number): [number, number] {
  return [
    clampCell(Math.floor((lo / span) * count), count),
    clampCell(Math.floor((hi / span) * count), count),
  ];
}

/**
 * Nearest visible node within `rPx` screen pixels of `(px, py)`, or -1.
 *
 * Probes only the buckets the search disc touches — 3×3 at a typical zoom, more when zoomed far
 * out (where each bucket is a few pixels wide), which is what keeps it correct at every zoom
 * rather than only the one it was tuned at. `clip` drops the nodes the painter culled, so an
 * off-window node cannot win and hide the dot the pointer is actually over.
 */
export function nearest(
  g: Grid,
  rx: Float32Array,
  frac: Float32Array,
  vis: Uint8Array,
  t: Transform,
  px: number,
  py: number,
  rPx: number,
  clip?: Clip,
): number {
  const qx = toRx(t, px);
  const qf = toFrac(t, py);
  const [cx0, cx1] = range(qx - rPx / Math.abs(t.kx), qx + rPx / Math.abs(t.kx), g.rxMax, g.cols);
  const [cy0, cy1] = range(qf - rPx / Math.abs(t.ky), qf + rPx / Math.abs(t.ky), 1, g.rows);
  let best = -1;
  let bestD = rPx * rPx;
  for (let cx = cx0; cx <= cx1; cx++) {
    const col = cx * g.cols;
    for (let cy = cy0; cy <= cy1; cy++) {
      const c = col + cy;
      for (let k = g.start[c]!, end = g.start[c + 1]!; k < end; k++) {
        const i = g.items[k]!;
        if (!vis[i]) {
          continue;
        }
        const nx = rx[i]! * t.kx + t.bx;
        const ny = frac[i]! * t.ky + t.by;
        if (clip && outside(clip, nx, ny)) {
          continue;
        }
        const dx = nx - px;
        const dy = ny - py;
        const d = dx * dx + dy * dy;
        if (d < bestD) {
          bestD = d;
          best = i;
        }
      }
    }
  }
  return best;
}

/**
 * Visible nodes inside a screen rectangle. The test happens in grid space — the transform is
 * affine and monotone in both axes, so the rectangle's corners map to a grid-space box.
 *
 * `clip` is intersected with the rectangle first: a marquee drawn past the plot area (which every
 * drag starting in the gutter is) must not select the nodes the painter culled off the edge, and a
 * rectangle that misses the painted box entirely selects nothing rather than collapsing onto an
 * edge and taking the whole column with it.
 */
export function inRect(
  g: Grid,
  rx: Float32Array,
  frac: Float32Array,
  vis: Uint8Array,
  t: Transform,
  rx0: number,
  ry0: number,
  rx1: number,
  ry1: number,
  clip?: Clip,
): number[] {
  let x0 = Math.min(rx0, rx1);
  let x1 = Math.max(rx0, rx1);
  let y0 = Math.min(ry0, ry1);
  let y1 = Math.max(ry0, ry1);
  if (clip) {
    x0 = Math.max(x0, clip.l);
    x1 = Math.min(x1, clip.r);
    y0 = Math.max(y0, clip.t);
    y1 = Math.min(y1, clip.b);
    if (x1 < x0 || y1 < y0) {
      return [];
    }
  }
  const ax = toRx(t, x0);
  const bx = toRx(t, x1);
  const af = toFrac(t, y0);
  const bf = toFrac(t, y1);
  const rxLo = Math.min(ax, bx);
  const rxHi = Math.max(ax, bx);
  const fLo = Math.min(af, bf);
  const fHi = Math.max(af, bf);
  const [cx0, cx1] = range(rxLo, rxHi, g.rxMax, g.cols);
  const [cy0, cy1] = range(fLo, fHi, 1, g.rows);
  const out: number[] = [];
  for (let cx = cx0; cx <= cx1; cx++) {
    const col = cx * g.cols;
    for (let cy = cy0; cy <= cy1; cy++) {
      const c = col + cy;
      for (let k = g.start[c]!, end = g.start[c + 1]!; k < end; k++) {
        const i = g.items[k]!;
        if (vis[i] && rx[i]! >= rxLo && rx[i]! <= rxHi && frac[i]! >= fLo && frac[i]! <= fHi) {
          out.push(i);
        }
      }
    }
  }
  return out;
}
